import React, { useEffect, useRef, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import * as S from "pages/main/BannerManager/BannerForm.style";
import { MAX_ACTIVE_BANNERS } from "pages/main/BannerManager/bannerState";
import {
  toFileKey,
  uploadToS3 as uploadFileWithPresigned,
} from "shared/api/awsS3";
import { getActiveBannerCount } from "shared/api/BannerApi";
import { usePostingPeriod } from "shared/lib/date/usePostingPeriod";
import { validateDateRange } from "shared/lib/date/validateDateRange";
import { useDirtyCheck } from "shared/lib/form/useDirtyCheck";
import CircularProgressIndicator from "shared/ui/CircularProgressIndicator";
import { alertDialog, confirmDialog, confirmLeaveIfDirty } from "shared/ui/feedback";
import { ChipGroup } from "shared/ui/filters";
import { PostingPeriodField } from "shared/ui/inputs";
import { FormPageShell } from "shared/ui/layout";
import { ImageUploadBox } from "shared/ui/upload";

const PC_SIZE = { width: 1440, height: 360 };
const MOB_SIZE = { width: 375, height: 240 };

// 모드별 문구. 생성/수정의 유일한 표면 차이를 한 곳에 모은다.
const COPY = {
  create: {
    header: "배너 등록하기",
    submit: "등록하기",
    confirm: "배너를 등록하시겠습니까?\n해당 정보로 배너가 등록됩니다.",
    leave: "배너 등록을 취소하시겠습니까?\n취소시 작성 중인 내용이 사라집니다.",
    publishBlock:
      "게시 중인 배너가 이미 3개입니다.\n'게시' 상태로는 더 등록할 수 없습니다. (미게시로는 등록 가능)",
    error: "배너 등록 중 오류가 발생했습니다.",
  },
  edit: {
    header: "배너 수정하기",
    submit: "수정하기",
    confirm: "배너를 수정하시겠습니까?\n해당 정보로 배너가 수정됩니다.",
    leave: "배너 수정을 취소하시겠습니까?\n취소시 변경 중인 내용이 사라집니다.",
    publishBlock:
      "게시 중인 배너가 이미 3개입니다.\n'게시' 상태로는 수정할 수 없습니다. (미게시로만 가능)",
    error: "배너 수정 중 오류가 발생했습니다.",
  },
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("이미지 로드 실패"));
    img.src = src;
  });

// 권장 사이즈(PC 1440×360, 모바일 375×240) 검증. 통과하지 못하면 안내 메시지 반환.
const validateDimensions = async (pcSrc, mobSrc) => {
  let pc;
  try {
    pc = await loadImage(pcSrc);
  } catch {
    return { ok: false, message: "PC 배너 이미지를 불러오지 못했습니다. 다시 선택해주세요." };
  }
  if (pc.width !== PC_SIZE.width || pc.height !== PC_SIZE.height) {
    return {
      ok: false,
      message: "PC 배너 이미지 사이즈가 맞지 않습니다. 1440*360 px 사이즈로 등록해주세요.",
    };
  }
  let mob;
  try {
    mob = await loadImage(mobSrc);
  } catch {
    return { ok: false, message: "모바일 배너 이미지를 불러오지 못했습니다. 다시 선택해주세요." };
  }
  if (mob.width !== MOB_SIZE.width || mob.height !== MOB_SIZE.height) {
    return {
      ok: false,
      message: "모바일 배너 이미지 사이즈가 맞지 않습니다. 375*240 px 사이즈로 등록해주세요.",
    };
  }
  return { ok: true };
};

// 게시기간(KST 벽시계)을 변환 없이 "YYYY-MM-DD HH:mm:ss"로 직렬화.
const convertToDatabaseFormat = (date) => {
  const parsedDate = new Date(date);
  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");
  const hours = String(parsedDate.getHours()).padStart(2, "0");
  const minutes = String(parsedDate.getMinutes()).padStart(2, "0");
  const seconds = String(parsedDate.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * 배너 등록·수정 공용 폼.
 *
 * 생성/수정의 차이는 (1) 초기값 (2) 이미지 업로드 정책 (3) 제출 동작 (4) 문구뿐이라,
 * 이를 props로 주입받아 한 컴포넌트로 처리한다. 데이터 로드/뮤테이션은 래퍼가 담당한다.
 *
 * @param {"create"|"edit"} mode
 * @param {object|null} initialValues - {title, hide, start, end, imagePc, imageMob}. edit는 로드 완료 전 null.
 * @param {object|null} loadedData    - edit 원본(파일 ID fallback + 자기 게시여부 판단용)
 * @param {boolean} isLoading
 * @param {string}  loadError
 * @param {(data:object)=>Promise<void>} onSubmit - 뮤테이션 호출(생성/수정)
 */
export function BannerForm({
  mode,
  initialValues = null,
  loadedData = null,
  isLoading = false,
  loadError = "",
  onSubmit,
}) {
  const copy = COPY[mode];
  const navigate = useNavigate();
  const period = usePostingPeriod();
  const { start, end, initialize: initializePeriod } = period;

  const [title, setTitle] = useState("");
  const [isPublished, setIsPublished] = useState(true); // true=미게시, false=게시
  const [filepc, setFilepc] = useState();
  const [filemob, setFilemob] = useState();
  const [imageViewpc, setImageViewpc] = useState();
  const [imageViewmob, setImageViewmob] = useState();
  const [submitting, setSubmitting] = useState(false);

  const { isDirty, captureBaseline } = useDirtyCheck({
    title,
    hide: isPublished,
    start: start || "",
    end: end || "",
    imagePc: imageViewpc || "",
    imageMob: imageViewmob || "",
  });

  // 게시(활성) 배너 개수만 조회 — "최대 3개" 검사용(목록 전체 fetch 대신).
  // 폼 진입 시마다 최신값이 필요하므로 전역 staleTime(60s)을 무시하고 항상 새로 받는다.
  const { data: activeBannerCount = 0 } = useQuery({
    queryKey: ["banners", "active-count"],
    queryFn: getActiveBannerCount,
    staleTime: 0,
  });

  // 초기값 1회 주입 + 변경 비교 기준값 기록 (create: 빈 값 / edit: 로드된 값).
  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current || !initialValues) return;
    initializedRef.current = true;
    setTitle(initialValues.title || "");
    setIsPublished(Boolean(initialValues.hide));
    initializePeriod(initialValues.start || "", initialValues.end || "");
    setImageViewpc(initialValues.imagePc || "");
    setImageViewmob(initialValues.imageMob || "");
    captureBaseline({
      title: initialValues.title || "",
      hide: Boolean(initialValues.hide),
      start: initialValues.start || "",
      end: initialValues.end || "",
      imagePc: initialValues.imagePc || "",
      imageMob: initialValues.imageMob || "",
    });
  }, [initialValues, initializePeriod, captureBaseline]);

  // 게시 중(활성) 배너는 최대 3개. 수정 대상이 이미 게시 중이면 자기 자신은 집계에서 제외
  // (이미 게시된 배너를 수정할 때 잘못 막히는 것 방지).
  const selfActive = mode === "edit" && loadedData?.hide === false ? 1 : 0;
  const cannotPublish = activeBannerCount - selfActive >= MAX_ACTIVE_BANNERS;

  // ImageUploadBox가 타입/용량 검사를 끝낸 File을 넘겨준다.
  const handleFileSelect = (selectedFile, setFile, setView) => {
    setFile(selectedFile);
    setView(URL.createObjectURL(selectedFile));
  };

  // 이미지 업로드 정책: create는 PC·모바일 모두 필수, edit는 새로 고른 것만 교체.
  const uploadImages = async () => {
    if (mode === "create") {
      if (!filepc || !filemob) {
        throw new Error("PC/모바일 이미지 파일이 모두 필요합니다.");
      }
      const [pcResult, mobResult] = await Promise.all([
        uploadFileWithPresigned(filepc, true),
        uploadFileWithPresigned(filemob, true),
      ]);
      // 서버가 webImageKey/mobileImageKey 필드에 S3 key를 기대한다.
      return {
        webImageKey: pcResult?.key || "",
        mobileImageKey: mobResult?.key || "",
        webImageFileId: pcResult?.fileId ?? null,
        mobileImageFileId: mobResult?.fileId ?? null,
      };
    }

    // edit: 새로 고른 것만 교체. 재업로드 안 한 기존 이미지는 전체 URL이라 key로 변환.
    const result = {
      webImageKey: toFileKey(imageViewpc),
      mobileImageKey: toFileKey(imageViewmob),
      webImageFileId: loadedData?.webImageFileId ?? null,
      mobileImageFileId: loadedData?.mobileImageFileId ?? null,
    };
    if (filepc) {
      const pcResult = await uploadFileWithPresigned(filepc, true);
      result.webImageKey = pcResult?.key || "";
      result.webImageFileId = pcResult?.fileId ?? null;
    }
    if (filemob) {
      const mobResult = await uploadFileWithPresigned(filemob, true);
      result.mobileImageKey = mobResult?.key || "";
      result.mobileImageFileId = mobResult?.fileId ?? null;
    }
    return result;
  };

  const handleLeave = async () => {
    const shouldProceed = await confirmLeaveIfDirty(isDirty, copy.leave);
    if (shouldProceed) navigate("/main/banner");
  };

  const handleSubmit = async () => {
    if (submitting) return;

    const dateRangeValidation = validateDateRange(start, end);
    if (!dateRangeValidation.isValid) {
      alertDialog(dateRangeValidation.message);
      return;
    }
    // 게시 중 3개 초과면 '게시'(isPublished===false) 전환 차단. 최종 강제는 백엔드.
    if (cannotPublish && isPublished === false) {
      alertDialog(copy.publishBlock);
      return;
    }
    if (title === "") {
      alertDialog("제목을 입력해주세요");
      return;
    }
    if (title.length > 255) {
      alertDialog("제목은 255자 제한입니다!");
      return;
    }

    const dimension = await validateDimensions(imageViewpc, imageViewmob);
    if (!dimension.ok) {
      alertDialog(dimension.message);
      return;
    }

    const shouldProceed = await confirmDialog(copy.confirm);
    if (!shouldProceed) return;

    try {
      setSubmitting(true);
      const images = await uploadImages();
      const data = {
        title,
        hide: isPublished,
        start: start ? convertToDatabaseFormat(start) : null,
        end: end ? convertToDatabaseFormat(end) : null,
        ...images,
      };
      await onSubmit(data);
      navigate("/main/banner");
    } catch (error) {
      const status = error?.response?.status;
      const serverMsg =
        typeof error?.response?.data === "string"
          ? error.response.data
          : error?.response?.data?.message;
      if (status === 401) {
        alertDialog("로그인이 필요합니다.");
      } else {
        alertDialog(serverMsg || error?.message || copy.error);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleTitleChange = (e) => {
    if (e.target.value.length <= 50) setTitle(e.target.value);
  };

  const isAllDataFilled = Boolean(
    title && imageViewmob && imageViewpc !== null && period.isComplete,
  );
  const canSubmit = isAllDataFilled && isDirty && !submitting;

  return (
    <FormPageShell
      title={copy.header}
      onBack={handleLeave}
      isLoading={isLoading}
      loadError={loadError}
      submitLabel={copy.submit}
      canSubmit={canSubmit}
      onSubmit={handleSubmit}
    >
      <CircularProgressIndicator loading={submitting} msg="저장 중.." />
      <S.RegisterDiv>
        <S.RowDiv>
          <S.Titlep>
            배너 타이틀 <S.Reddot>&nbsp;*</S.Reddot>
          </S.Titlep>
          <S.TextInput
            type="text"
            placeholder="제목입력"
            value={title}
            onChange={handleTitleChange}
          />
        </S.RowDiv>
        <S.RowDiv>
          <S.Pubp>
            게시 여부<S.Reddot>&nbsp;*</S.Reddot>
          </S.Pubp>
          <ChipGroup
            options={[
              { label: "게시", value: false, disabled: cannotPublish },
              { label: "미게시", value: true },
            ]}
            value={isPublished}
            onChange={setIsPublished}
            allowDeselect={false}
          />
          {cannotPublish && (
            <S.PublishHint>
              게시 중 배너가 이미 3개라 게시할 수 없습니다. (미게시로만 가능)
            </S.PublishHint>
          )}
        </S.RowDiv>
        <S.RowDiv333>
          <S.Datep>게시기간</S.Datep>
          <PostingPeriodField
            enabled={period.enabled}
            onToggle={period.setEnabled}
            start={period.start}
            end={period.end}
            onStartChange={period.handleStartChange}
            onEndChange={period.handleEndChange}
          />
        </S.RowDiv333>
      </S.RegisterDiv>
      <S.ImgDiv>
        <ImageUploadBox
          imageUrl={imageViewpc || ""}
          onSelectFile={(file) => handleFileSelect(file, setFilepc, setImageViewpc)}
          onRemove={() => {
            setFilepc(null);
            setImageViewpc(null);
          }}
          title="배너 이미지 삽입(PC뷰)"
          sizeHint="이미지 사이즈(1440*360 px 권장)"
          width="60%"
          height="205px"
        />
        <ImageUploadBox
          imageUrl={imageViewmob || ""}
          onSelectFile={(file) => handleFileSelect(file, setFilemob, setImageViewmob)}
          onRemove={() => {
            setFilemob(null);
            setImageViewmob(null);
          }}
          title="배너 이미지 삽입(모바일뷰)"
          sizeHint="이미지 사이즈(375*240 px 권장)"
          width="40%"
          height="205px"
        />
      </S.ImgDiv>
    </FormPageShell>
  );
}

export default BannerForm;
